import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Link as LinkIcon, LinkIcon as Link2Icon, ImageIcon, Paperclip, Unlink } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './dialog'
import { cn } from '@/lib/utils'
import api from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import { useEffect, useRef, useCallback, useState } from 'react'

export interface UploadedAttachment {
    name: string
    url: string
    type: string
    size?: number
}

interface RichTextEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    /** Enable image embed button (inserts image inline in HTML) */
    enableImageEmbed?: boolean
    /** Enable file attach button (uploads file, returns attachment metadata) */
    enableAttachments?: boolean
    /** Directory on the image server to upload to (default: 'RunbookAssignments') */
    imageUploadDirectory?: string
    /** Callback when a file is attached (not embedded) */
    onAttach?: (attachment: UploadedAttachment) => void
}

async function uploadToImageServer(file: File, directory: string): Promise<{ url: string }> {
    // Presign + upload the bytes directly to S3 (bypasses CloudFront/WAF, whose
    // body inspection intermittently false-blocks binary uploads).
    const contentType = file.type || 'application/octet-stream'
    const presign = await api.post<{ uploadUrl: string; key: string; url: string; cacheControl: string }>(
        '/api/images/presign',
        { directory, imageName: file.name, contentType },
    )

    const putRes = await fetch(presign.data.uploadUrl, {
        method: 'PUT',
        body: file,
        // Sent because the stored object needs them — a presigned PUT enforces neither, and an
        // omitted Cache-Control leaves the object on CloudFront's 24-hour default TTL.
        headers: { 'Content-Type': contentType, 'Cache-Control': presign.data.cacheControl },
    })
    if (!putRes.ok) {
        throw new Error(`Upload failed: ${putRes.status}`)
    }

    // Normalize the CloudFront URL to a relative path (editor stores relative paths).
    let url = presign.data.url
    try {
        url = new URL(url).pathname
    } catch {
        // already relative
    }

    return { url }
}

export function RichTextEditor({
    value,
    onChange,
    placeholder,
    className,
    enableImageEmbed: imageEmbedWanted = false,
    enableAttachments: attachmentsWanted = false,
    imageUploadDirectory = 'RunbookAssignments',
    onAttach,
}: RichTextEditorProps) {
    // An upload is a presigned write to the image store. For RunbookAssignments (images in a
    // recovery update, a runbook, an object's notes) the server allows the people who write those;
    // anywhere else it needs graphics.create. Without it, nothing offers an upload that would fail.
    const canUploadAssignmentImage = useCan(
        'graphics.create', 'recovery.execute', 'recover-requests.create', 'recover-requests.update',
        'master-runbooks.create', 'master-runbooks.update', 'objects.update', 'teams.create', 'teams.update',
    )
    const canUploadGraphic = useCan('graphics.create')
    const canUpload = imageUploadDirectory === 'RunbookAssignments' ? canUploadAssignmentImage : canUploadGraphic
    const enableImageEmbed = imageEmbedWanted && canUpload
    const enableAttachments = attachmentsWanted && canUpload
    const embedInputRef = useRef<HTMLInputElement>(null)
    const attachInputRef = useRef<HTMLInputElement>(null)
    const uploadDirRef = useRef(imageUploadDirectory)
    const enableImageEmbedRef = useRef(enableImageEmbed)
    uploadDirRef.current = imageUploadDirectory
    enableImageEmbedRef.current = enableImageEmbed

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2],
                },
                // StarterKit (v3) bundles link & underline; disable them here so the
                // explicitly-configured versions below are the only ones (avoids the
                // "Duplicate extension names" tiptap warning).
                link: false,
                underline: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    class: 'text-primary underline',
                },
            }),
            Image.configure({
                inline: false,
                allowBase64: false,
                HTMLAttributes: {
                    class: 'max-w-full h-auto rounded',
                },
            }),
            Placeholder.configure({
                placeholder: placeholder || 'Enter text...',
            }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
        editorProps: {
            attributes: {
                class: 'focus:outline-none min-h-[200px] p-4 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:pl-6 [&_ul]:[list-style-type:disc] [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:pl-6 [&_ol]:[list-style-type:decimal] [&_li]:my-0.5 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2 [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2',
            },
            handlePaste: (view, event) => {
                // Handle pasted images from clipboard
                if (!enableImageEmbedRef.current) return false
                const items = event.clipboardData?.items
                if (!items) return false

                for (const item of Array.from(items)) {
                    if (item.type.startsWith('image/')) {
                        event.preventDefault()
                        const file = item.getAsFile()
                        if (!file) continue

                        // Upload and insert asynchronously
                        uploadToImageServer(file, uploadDirRef.current).then(({ url }) => {
                            view.dispatch(
                                view.state.tr.replaceSelectionWith(
                                    view.state.schema.nodes.image.create({ src: url, alt: file.name })
                                )
                            )
                        }).catch((err) => {
                            console.error('Paste image upload failed:', err)
                        })
                        return true
                    }
                }
                return false
            },
            handleDrop: (view, event) => {
                // Handle dropped image files
                if (!enableImageEmbedRef.current) return false
                const files = event.dataTransfer?.files
                if (!files?.length) return false

                for (const file of Array.from(files)) {
                    if (file.type.startsWith('image/')) {
                        event.preventDefault()
                        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })

                        uploadToImageServer(file, uploadDirRef.current).then(({ url }) => {
                            const node = view.state.schema.nodes.image.create({ src: url, alt: file.name })
                            const pos = coords?.pos ?? view.state.selection.from
                            view.dispatch(view.state.tr.insert(pos, node))
                        }).catch((err) => {
                            console.error('Drop image upload failed:', err)
                        })
                        return true
                    }
                }
                return false
            },
        },
    })

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '')
        }
    }, [value, editor])

    const [linkDialogOpen, setLinkDialogOpen] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')

    const handleOpenLinkDialog = () => {
        if (!editor) return
        const previousUrl = editor.getAttributes('link').href || ''
        setLinkUrl(previousUrl)
        setLinkDialogOpen(true)
    }

    const handleConfirmLink = () => {
        if (!editor) return
        if (linkUrl.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run()
        }
        setLinkDialogOpen(false)
        setLinkUrl('')
    }

    const handleRemoveLink = () => {
        if (!editor) return
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        setLinkDialogOpen(false)
        setLinkUrl('')
    }

    // Embed: upload image and insert inline in the HTML
    const handleImageEmbed = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!editor) return
        const file = event.target.files?.[0]
        if (!file) return

        const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
        if (!allowed.includes(file.type)) {
            alert('Please select a valid image file (PNG, JPEG, GIF, WebP)')
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('Image must be less than 5MB')
            return
        }

        try {
            const { url } = await uploadToImageServer(file, imageUploadDirectory)
            editor.chain().focus().setImage({ src: url, alt: file.name }).run()
        } catch (err) {
            console.error('Image embed failed:', err)
            alert('Failed to upload image. Please try again.')
        }

        if (embedInputRef.current) embedInputRef.current.value = ''
    }, [editor, imageUploadDirectory])

    // Attach: upload file and notify parent via onAttach callback
    const handleFileAttach = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/plain']
        if (!allowed.includes(file.type)) {
            alert('Supported file types: PNG, JPEG, GIF, WebP, PDF, TXT')
            return
        }
        if (file.size > 10 * 1024 * 1024) {
            alert('File must be less than 10MB')
            return
        }

        try {
            const { url } = await uploadToImageServer(file, imageUploadDirectory)
            onAttach?.({
                name: file.name,
                url,
                type: file.type,
                size: file.size,
            })
        } catch (err) {
            console.error('File attach failed:', err)
            alert('Failed to upload file. Please try again.')
        }

        if (attachInputRef.current) attachInputRef.current.value = ''
    }, [imageUploadDirectory, onAttach])

    if (!editor) {
        return null
    }

    return (
        <div className={cn("border rounded-md", className)}>
            <div className="flex items-center gap-1 p-2 border-b bg-muted/50 flex-wrap">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('bold') && 'bg-muted')}
                    title="Bold"
                >
                    <Bold className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('italic') && 'bg-muted')}
                    title="Italic"
                >
                    <Italic className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('underline') && 'bg-muted')}
                    title="Underline"
                >
                    <UnderlineIcon className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('heading', { level: 1 }) && 'bg-muted')}
                    title="Heading 1"
                >
                    <Heading1 className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('heading', { level: 2 }) && 'bg-muted')}
                    title="Heading 2"
                >
                    <Heading2 className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('bulletList') && 'bg-muted')}
                    title="Bullet List"
                >
                    <List className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={cn("h-8 w-8 p-0", editor.isActive('orderedList') && 'bg-muted')}
                    title="Numbered List"
                >
                    <ListOrdered className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenLinkDialog}
                    className={cn("h-8 w-8 p-0", editor.isActive('link') && 'bg-muted')}
                    title="Insert Link"
                >
                    <LinkIcon className="h-4 w-4" />
                </Button>
                {enableImageEmbed && (
                    <>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => embedInputRef.current?.click()}
                            className="h-8 w-8 p-0"
                            title="Embed Image"
                        >
                            <ImageIcon className="h-4 w-4" />
                        </Button>
                        <input
                            ref={embedInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            className="hidden"
                            onChange={handleImageEmbed}
                        />
                    </>
                )}
                {enableAttachments && (
                    <>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => attachInputRef.current?.click()}
                            className="h-8 w-8 p-0"
                            title="Attach File"
                        >
                            <Paperclip className="h-4 w-4" />
                        </Button>
                        <input
                            ref={attachInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain"
                            className="hidden"
                            onChange={handleFileAttach}
                        />
                    </>
                )}
            </div>
            <EditorContent editor={editor} />

            {/* Link dialog */}
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Insert Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="space-y-2">
                            <Label htmlFor="link-url">URL</Label>
                            <Input
                                id="link-url"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                placeholder="https://example.com"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmLink() } }}
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        {editor?.isActive('link') && (
                            <Button type="button" variant="destructive" size="sm" onClick={handleRemoveLink}>
                                <Unlink className="h-4 w-4 mr-1" />
                                Remove Link
                            </Button>
                        )}
                        <div className="flex-1" />
                        <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleConfirmLink}>
                            Apply
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
